import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { Stack } from '@ekhodealer/ekho-common/rev';

import type { CommonDateFilter as LeadsDateFilter } from '@common/types/filters';

import { AccordionSection } from '@common/components/AccordionSection';
import { CommonDateFilterSection } from '@common/components/FilterGroups/CommonDateFilterSection';

import {
	dateCreatedCustomRangeAtom,
	dateCreatedFilterAtom,
	lastUpdatedCustomRangeAtom,
	lastUpdatedFilterAtom,
	updateDateCreatedCustomRangeAtom,
	updateDateCreatedFilterAtom,
	updateLastUpdatedCustomRangeAtom,
	updateLastUpdatedFilterAtom,
} from '../../atoms/leadsAtoms';

/**
 * FiltersDrawerDatesSection component that provides date filtering options
 * wrapped in an accordion
 */
export function FiltersDrawerDatesSection(): React.JSX.Element {
	const dateCreatedFilter = useAtomValue(dateCreatedFilterAtom);
	const dateCreatedCustomRange = useAtomValue(dateCreatedCustomRangeAtom);
	const lastUpdatedFilter = useAtomValue(lastUpdatedFilterAtom);
	const lastUpdatedCustomRange = useAtomValue(lastUpdatedCustomRangeAtom);

	const setDateCreatedFilter = useSetAtom(updateDateCreatedFilterAtom);
	const setDateCreatedCustomRange = useSetAtom(updateDateCreatedCustomRangeAtom);
	const setLastUpdatedFilter = useSetAtom(updateLastUpdatedFilterAtom);
	const setLastUpdatedCustomRange = useSetAtom(updateLastUpdatedCustomRangeAtom);

	const handleDateCreatedChange = useCallback(
		(value: LeadsDateFilter) => {
			setDateCreatedFilter(value);
		},
		[setDateCreatedFilter],
	);

	const handleLastUpdatedChange = useCallback(
		(value: LeadsDateFilter) => {
			setLastUpdatedFilter(value);
		},
		[setLastUpdatedFilter],
	);

	// Determine if this section has any applied filters
	const hasAppliedFilters =
		(dateCreatedFilter !== null && dateCreatedFilter !== undefined) ||
		(lastUpdatedFilter !== null && lastUpdatedFilter !== undefined);

	return (
		<AccordionSection title='Dates' defaultExpanded={hasAppliedFilters}>
			<Stack itemsSpacing='24'>
				<CommonDateFilterSection
					label='Date created'
					filterValue={dateCreatedFilter}
					onFilterChange={handleDateCreatedChange}
					customRange={dateCreatedCustomRange}
					onCustomRangeChange={setDateCreatedCustomRange}
				/>
				<CommonDateFilterSection
					label='Last updated'
					filterValue={lastUpdatedFilter}
					onFilterChange={handleLastUpdatedChange}
					customRange={lastUpdatedCustomRange}
					onCustomRangeChange={setLastUpdatedCustomRange}
				/>
			</Stack>
		</AccordionSection>
	);
}
